import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import Dashboard from "@/pages/Dashboard";
import StackGenerator from "@/pages/StackGenerator";
import Library from "@/pages/Library";
import VisualsEnhanced from "@/pages/VisualsEnhanced";
import MirrorsSync from "@/pages/MirrorsSync";
import AdminVisuals from "@/pages/AdminVisuals";
import AdminDiscography from "@/pages/AdminDiscography";
import FrequencyGiftReceiver from "@/pages/FrequencyGiftReceiver";
import FrequencyBundleReceiver from "@/pages/FrequencyBundleReceiver";
import HowItWorks from "@/pages/HowItWorks";
import ResonanceHub from "@/pages/ResonanceHub";
import BrainPage from "@/pages/BrainPage";
import GodmodePage from "@/pages/GodmodePage";
import CodesPage from "@/pages/CodesPage";
import SoundPage from "@/pages/SoundPage";
import AuthPage from "@/pages/AuthPage";
import AuthCallback from "@/pages/AuthCallback";
import AboutPage from "@/pages/AboutPage";
import PrivacyPage from "@/pages/PrivacyPage";
import TermsPage from "@/pages/TermsPage";
import MobiusNav from "@/components/MobiusNav";
import CodesDirectConnect from "@/components/CodesDirectConnect";
import Paywall from "@/components/Paywall";
import AppStoreBadge from "@/components/AppStoreBadge";
import { AuthProvider, useAuth } from "@/lib/AuthContext";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Toaster } from "@/components/ui/sonner";
import { LogOut } from "lucide-react";

const LiveClock = () => {
  const [now, setNow] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);
  const time = now.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });
  const date = now.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  return (
    <div className="flex items-center gap-2 text-[10px] sm:text-xs font-mono tracking-widest text-[#FF0000]/70 shrink-0 ml-auto" data-testid="live-clock">
      <span>{date}</span>
      <span className="text-[#FF0000]">{time}</span>
    </div>
  );
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || ''; 
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

const Shell = ({ children, gated = false }) => {
  const [pttOpen, setPttOpen] = useState(false);
  const { user, logout, subscriptionLabel } = useAuth();
  const subLabel = subscriptionLabel ? subscriptionLabel() : "";

  useEffect(() => { axios.get(`${API}/`).catch(()=>{}); }, []);
  
  return (
    <div className="min-h-screen pb-16">
      <div className="bridget-riley-bg" />
      <header className="sticky top-0 z-50 w-full border-b border-[#FF0000]/15 bg-black/95 backdrop-blur" data-testid="top-header">
        <div className="mx-auto max-w-6xl px-3 sm:px-6 py-2">
          <div className="flex flex-wrap sm:flex-nowrap items-center gap-x-3 gap-y-2">
            <div className="flex items-center gap-2 shrink-0">
              <Link to="/" className="shrink-0" aria-label="Go home" data-testid="brand-home-link">
                <img src={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/static/godmode_icon.jpg`}
                  alt="GODMODE SOUND"
                  className="h-7 w-7 object-cover shrink-0 rounded hover:opacity-80 transition-opacity"
                  style={{ filter: 'drop-shadow(0 0 6px rgba(255, 0, 0, 0.5))' }}
                  loading="eager"
                  referrerPolicy="no-referrer"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = "https://customer-assets.emergentagent.com/job_3a6acb3e-074e-41f3-a0be-676978ae6151/artifacts/m4j4rtmm_MIRRORS_1024x1024.jpg";
                  }}
                  data-testid="header-godmode-icon"
                />
              </Link>
              <button onClick={() => setPttOpen(true)} className="shrink-0" data-testid="brand-ptt-trigger" aria-label="Open CODES Direct Connect">
                <img src={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/static/mirrors_id.jpeg`}
                  alt="MIRRORS"
                  className="h-8 w-auto object-contain shrink-0 rounded hover:opacity-80 transition-opacity"
                  style={{ filter: 'drop-shadow(0 0 4px rgba(255, 0, 0, 0.3))' }}
                  loading="eager"
                  referrerPolicy="no-referrer"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = "https://customer-assets.emergentagent.com/job_3a6acb3e-074e-41f3-a0be-676978ae6151/artifacts/up1wyv92_MIRRORS%20ID.jpeg";
                  }}
                  data-testid="header-mirrors-icon"
                />
              </button>
            </div>
            <div className="order-3 sm:order-none flex items-center gap-1.5 flex-wrap w-full sm:w-auto min-w-0">
              <a href="https://earth.google.com/web/" target="_blank" rel="noopener noreferrer" className="gm-chip" data-testid="tag-godmode">GODMODE</a>
              <Link to="/mirrors" className="gm-chip" data-testid="tag-mirrors">MIRRORS</Link>
              <Link to="/sound" className="gm-chip" data-testid="tag-906">90.6</Link>
              <Link to="/library" className="gm-chip" data-testid="tag-archive">ARCHIVE</Link>
              <Link to="/mirrors?tab=sync" className="gm-chip" data-testid="tag-codes">CODES</Link>
              {subLabel && (
                <span
                  className="gm-chip"
                  style={{ borderColor: 'rgba(255, 0, 0, 0.6)', background: 'rgba(255, 0, 0, 0.08)', cursor: 'default' }}
                  data-testid="subscription-status-chip"
                  title="Your current access tier"
                >
                  {subLabel}
                </span>
              )}
              {user && (
                <button onClick={logout} className="gm-chip gm-chip--ghost flex items-center gap-1" data-testid="logout-btn" aria-label="Sign out">
                  <LogOut className="w-3 h-3" />
                </button>
              )}
              {!user && user !== null && (
                <Link to="/auth" className="gm-chip gm-chip--ghost" data-testid="signin-link">SIGN IN</Link>
              )}
            </div>
            <div className="flex items-center gap-2 ml-auto shrink-0">
              <span
                className="hidden sm:inline-flex items-center gap-1 px-1.5 py-0.5 border border-[#FF0000]/25 rounded-sm text-[9px] font-mono tracking-[0.22em] uppercase text-[#FF0000]/60"
                title="A = 432 Hz tuning · latency-free visual sync"
                data-testid="tuning-badge"
              >
                <span className="w-1 h-1 rounded-full bg-[#FF0000] animate-pulse" />
                A·432
              </span>
              <LiveClock />
            </div>
          </div>
        </div>
      </header>

      <main className="mac-terminal-container" style={{ paddingBottom: "100px" }}>
        {gated ? <Paywall>{children}</Paywall> : children}
      </main>
      <AppStoreBadge />
      <MobiusNav />

      {/* Legal Disclaimer Footer */}
      <div className="fixed bottom-0 left-0 right-0 z-30 bg-black/95 border-t border-[#FF0000]/8 py-1.5 px-4 text-center pointer-events-none" data-testid="legal-disclaimer">
        <p className="text-[7px] font-mono text-[#FF0000]/20 tracking-wider leading-relaxed max-w-3xl mx-auto">
          MIRRORS is a sound experience for focus, calm, and presence. It is not a medical device and makes no therapeutic claims. Not intended to diagnose, treat, cure, or prevent any condition.
        </p>
      </div>

      <Dialog open={pttOpen} onOpenChange={setPttOpen}>
        <DialogContent className="sm:max-w-md bg-black/98 border-[#FF0000]/30 p-0 overflow-hidden" data-testid="ptt-modal">
          <div className="p-4 border-b border-[#FF0000]/15">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-[#FF0000]/15 border border-[#FF0000]/40 flex items-center justify-center">
                <span className="text-[9px] font-mono font-bold text-[#FF0000]">DC</span>
              </div>
              <div>
                <div className="text-sm font-mono font-bold text-[#FF0000] tracking-wider">DIRECT CONNECT</div>
                <div className="text-[8px] font-mono text-[#FF0000]/30 tracking-widest">CODES by GODMODE LLC</div>
              </div>
            </div>
          </div>
          <div className="p-4 max-h-[75vh] overflow-y-auto">
            <CodesDirectConnect compact />
          </div>
        </DialogContent>
      </Dialog>

      <Toaster richColors position="top-right"/>
    </div>
  );
};

function AppRoutes() {
  const location = useLocation();
  // CRITICAL: detect Google OAuth callback synchronously (NOT in useEffect)
  // to avoid race conditions with AuthProvider's /auth/me check.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      {/* Free pages */}
      <Route path="/" element={<Shell><Dashboard /></Shell>} />
      <Route path="/sound" element={<Shell><SoundPage /></Shell>} />
      <Route path="/library" element={<Shell><Library /></Shell>} />
      <Route path="/archive" element={<Navigate to="/library" replace />} />
      <Route path="/how-it-works" element={<Shell><HowItWorks /></Shell>} />
      <Route path="/codes" element={<Shell><CodesPage /></Shell>} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/about" element={<Shell><AboutPage /></Shell>} />
      <Route path="/privacy" element={<Shell><PrivacyPage /></Shell>} />
      <Route path="/terms" element={<Shell><TermsPage /></Shell>} />

      {/* Gated pages — require login + subscription or GODMODE code */}
      <Route path="/stack" element={<Shell gated><StackGenerator /></Shell>} />
      <Route path="/visuals" element={<Shell gated><VisualsEnhanced /></Shell>} />
      <Route path="/resonance" element={<Shell gated><ResonanceHub /></Shell>} />
      <Route path="/brain" element={<Shell gated><BrainPage /></Shell>} />
      <Route path="/godmode" element={<Shell gated><GodmodePage /></Shell>} />
      <Route path="/mirrors" element={<Shell gated><MirrorsSync /></Shell>} />
      <Route path="/admin/visuals" element={<Shell gated><AdminVisuals /></Shell>} />
      <Route path="/admin/discography" element={<Shell><AdminDiscography /></Shell>} />
      <Route path="/gift/:giftId" element={<Shell><FrequencyGiftReceiver /></Shell>} />
      <Route path="/bundle/:bundleIds" element={<Shell><FrequencyBundleReceiver /></Shell>} />
      <Route path="/entrainment" element={<Navigate to="/resonance" replace />} />
      <Route path="/tools" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
